import { useParams } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatCurrency } from "@/config/company.config";
import { 
  Users, Briefcase, Calendar, DollarSign, TrendingUp, Clock,
  CheckCircle, AlertTriangle, FileText, Video, Scale, Plane,
  FileCheck, Calculator, BarChart3
} from "lucide-react";
import { OnboardingGuide } from '@/components/onboarding/OnboardingGuide';
import { useNavigate } from 'react-router-dom';

const partnerConfig: Record<string, { icon: any; name: { en: string; fr: string }; color: string }> = {
  "business-law": { icon: Scale, name: { en: "Business Law", fr: "Droit des Affaires" }, color: "text-blue-600" },
  "immigration-law": { icon: Plane, name: { en: "Immigration Law", fr: "Droit de l'Immigration" }, color: "text-green-600" },
  "notary": { icon: FileCheck, name: { en: "Notary", fr: "Notaire" }, color: "text-purple-600" },
  "tax-advisor": { icon: Calculator, name: { en: "Tax Advisor", fr: "Conseiller Fiscal" }, color: "text-orange-600" },
  "cpa": { icon: BarChart3, name: { en: "CPA", fr: "CPA" }, color: "text-indigo-600" },
};

const translations = {
  en: {
    welcome: "Welcome back",
    dashboardSubtitle: "Here's your activity summary",
    activeClients: "Active Clients",
    activeMandates: "Active Mandates",
    upcomingMeetings: "Upcoming Meetings",
    monthlyRevenue: "Monthly Revenue",
    pendingTasks: "Pending Tasks",
    completedThisMonth: "Completed This Month",
    urgentMandates: "Urgent Mandates",
    hoursLogged: "Hours Logged",
    recentActivity: "Recent Activity",
    quickActions: "Quick Actions",
    startVideoCall: "Start Video Call",
    scheduleNewMeeting: "Schedule Meeting",
    createMandate: "Create Mandate",
    viewDocuments: "View Documents",
    performanceOverview: "Performance Overview",
    thisWeek: "this week",
    thisMonth: "this month",
    comparedToLastMonth: "compared to last month",
  },
  fr: {
    welcome: "Bienvenue",
    dashboardSubtitle: "Voici le résumé de votre activité",
    activeClients: "Clients Actifs",
    activeMandates: "Mandats Actifs",
    upcomingMeetings: "Réunions à Venir",
    monthlyRevenue: "Revenu Mensuel",
    pendingTasks: "Tâches en Attente",
    completedThisMonth: "Complétés ce Mois",
    urgentMandates: "Mandats Urgents",
    hoursLogged: "Heures Enregistrées",
    recentActivity: "Activité Récente",
    quickActions: "Actions Rapides",
    startVideoCall: "Démarrer Visio",
    scheduleNewMeeting: "Planifier Réunion",
    createMandate: "Créer Mandat",
    viewDocuments: "Voir Documents",
    performanceOverview: "Aperçu Performance",
    thisWeek: "cette semaine",
    thisMonth: "ce mois",
    comparedToLastMonth: "comparé au mois dernier",
  },
};

// Mock stats data
const mockStats = {
  activeClients: 12,
  activeMandates: 8,
  upcomingMeetings: 5,
  monthlyRevenue: 45000,
  pendingTasks: 15,
  completedThisMonth: 6,
  urgentMandates: 2,
  hoursLogged: 124,
  revenueGrowth: 12.5,
};

export default function PartnerOverview() {
  const { partnerType } = useParams<{ partnerType: string }>();
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const t = translations[language as keyof typeof translations] || translations.en;

  const config = partnerType ? partnerConfig[partnerType] : null;
  const Icon = config?.icon || Briefcase;
  const partnerName = config?.name?.[language as keyof typeof config.name] || config?.name?.en || "Partner";
  const iconColor = config?.color || "text-primary";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5`}>
          <Icon className={`h-8 w-8 ${iconColor}`} />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t.welcome}, {user?.displayName || user?.email?.split('@')[0]}
          </h1>
          <p className="text-muted-foreground">{partnerName} • {t.dashboardSubtitle}</p>
        </div>
      </div>

      <OnboardingGuide
        role="partner"
        language={language as 'en' | 'fr'}
        partnerType={partnerType || 'business-law'}
      />

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">{t.activeClients}</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mockStats.activeClients}</div>
            <p className="text-xs text-muted-foreground">+3 {t.thisMonth}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">{t.activeMandates}</CardTitle>
            <Briefcase className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mockStats.activeMandates}</div>
            <div className="flex items-center gap-1 text-xs">
              <Badge variant="destructive" className="text-xs px-1.5 py-0">
                {mockStats.urgentMandates} urgent
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">{t.upcomingMeetings}</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mockStats.upcomingMeetings}</div>
            <p className="text-xs text-muted-foreground">{t.thisWeek}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">{t.monthlyRevenue}</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(mockStats.monthlyRevenue)}</div>
            <div className="flex items-center gap-1 text-xs text-green-600">
              <TrendingUp className="h-3 w-3" />
              +{mockStats.revenueGrowth}% {t.comparedToLastMonth}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-950/20 dark:to-amber-900/10 border-amber-200">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="p-2 bg-amber-500/10 rounded-lg">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{mockStats.hoursLogged}h</p>
              <p className="text-sm text-muted-foreground">{t.hoursLogged}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-950/20 dark:to-blue-900/10 border-blue-200">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{mockStats.pendingTasks}</p>
              <p className="text-sm text-muted-foreground">{t.pendingTasks}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-950/20 dark:to-green-900/10 border-green-200">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="p-2 bg-green-500/10 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{mockStats.completedThisMonth}</p>
              <p className="text-sm text-muted-foreground">{t.completedThisMonth}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100/50 dark:from-purple-950/20 dark:to-purple-900/10 border-purple-200">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="p-2 bg-purple-500/10 rounded-lg">
              <Video className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">3</p>
              <p className="text-sm text-muted-foreground">{t.startVideoCall}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity & Quick Actions */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t.recentActivity}</CardTitle>
            <CardDescription>
              {language === 'fr' ? 'Dernières actions et mises à jour' : 'Latest actions and updates'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {[
              { icon: CheckCircle, text: language === 'fr' ? 'Mandat #1234 complété' : 'Mandate #1234 completed', time: '2h ago', color: 'text-green-600' },
              { icon: Calendar, text: language === 'fr' ? 'Réunion avec Martin Corp.' : 'Meeting with Martin Corp.', time: '4h ago', color: 'text-blue-600' },
              { icon: FileText, text: language === 'fr' ? 'Document NDA signé' : 'NDA document signed', time: '1d ago', color: 'text-purple-600' },
              { icon: Users, text: language === 'fr' ? 'Nouveau client ajouté' : 'New client added', time: '2d ago', color: 'text-orange-600' },
            ].map((activity, idx) => (
              <div key={idx} className="flex items-center gap-3 py-2 border-b last:border-0">
                <activity.icon className={`h-5 w-5 ${activity.color}`} />
                <div className="flex-1">
                  <p className="text-sm font-medium">{activity.text}</p>
                  <p className="text-xs text-muted-foreground">{activity.time}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t.quickActions}</CardTitle>
            <CardDescription>
              {language === 'fr' ? 'Accès rapide aux fonctionnalités courantes' : 'Quick access to common features'}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <button className="flex flex-col items-center justify-center gap-2 p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
              <Video className="h-6 w-6 text-primary" />
              <span className="text-sm font-medium">{t.startVideoCall}</span>
            </button>
            <button className="flex flex-col items-center justify-center gap-2 p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
              <Calendar className="h-6 w-6 text-primary" />
              <span className="text-sm font-medium">{t.scheduleNewMeeting}</span>
            </button>
            <button className="flex flex-col items-center justify-center gap-2 p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
              <Briefcase className="h-6 w-6 text-primary" />
              <span className="text-sm font-medium">{t.createMandate}</span>
            </button>
            <button className="flex flex-col items-center justify-center gap-2 p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
              <FileText className="h-6 w-6 text-primary" />
              <span className="text-sm font-medium">{t.viewDocuments}</span>
            </button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
